import { PlusOutlined, UnorderedListOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Card, Divider, Drawer, Form, Input, List, Select, message, Upload } from "antd";
import { useEffect, useRef, useState } from "react";

import DrawerFooter from "../../../components/layout/drawer/DrawerFooter";
import requiredRule from "../../../utils/formRules/requiredRule";
import GeneralModal from "../../../utils/GeneralModal";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";
import fileToBase64 from "../utils/fileToBase64";
import { createUploadProps } from "../utils/uploadProps";
import { ActionType, MethodType, PerformersType } from "./content/faceCallTypes";
import { faceCallPerformerMutation, faceCallPerformerDelete } from "./services/faceCall.services";
import "./performersTabContent.scss";

const { Search } = Input;
const { Meta } = Card;

interface PerformersTabContentProps {
  performersData: PerformersType[];
  setActiveKey: Function;
  setSelectPerformers: Function;
  isAdmin: boolean,
  refreshData: () => void,
}

const PerformersTabContent = ({
  performersData,
  setActiveKey,
  setSelectPerformers,
  isAdmin,
  refreshData
}: PerformersTabContentProps) => {
  const [form] = Form.useForm();
  const [displayData, setDisplayData] = useState<PerformersType[] | any>([]);
  const [editData, setEditData] = useState<PerformersType | undefined>(undefined);
  const [modalAction, setModalAction] = useState<ActionType>("Create");
  const [showDrawer, setShowDrawer] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const coverRef = useRef<any>();

  const handleSearch = () => {
    const filteredData =
      performersData?.filter((performer: PerformersType) =>
        performer.performerName
          ?.toLowerCase()
          ?.includes(searchText.toLowerCase())
      ) || [];

    setDisplayData(filteredData);
  };

  const handleEditClick = (record: PerformersType) => {
    setShowDrawer(true);
    setModalAction("Edit");
    setEditData(record);
  };

  const handleListClick = (record: PerformersType) => {
    setActiveKey("1");
    setSelectPerformers([record.performerName]);
  };

  const handleDeleteClick = (record: PerformersType) => {
    if (record.totalVideosCount > 1) setShowDeleteModal(true);
    else handleDelete(record.performerId);
  };

  const handleClose = () => {
    form.resetFields();
    setEditData(undefined);
    setShowDrawer(false);
  }

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  }

  const handleDelete = async (performerId: number) => {
    const data = await faceCallPerformerDelete(performerId);

    if (data?.status === "SUCCESS") {
      refreshData();
      handleClose();
      message.success(renderAntDMessageConfig("Successful"));
    } else {
      message.error(renderAntDMessageConfig("Failed"));
    }
  }

  const handleSubmit = async (values: any) => {
    let method: MethodType;
    let payload;
    let photoFilePayload;
    let avatarImageFilePayload;

    const photoFile = values.photoFile[0];
    const avatarImageFile = values.avatarImageFile?.[0];

    if (photoFile.originFileObj) {
      const base64File = await fileToBase64(photoFile.originFileObj);
      photoFilePayload = {
        fileName: photoFile.name,
        base64File
      }
    } else {
      photoFilePayload = {
        fileName: photoFile.name
      }
    }

    if (avatarImageFile?.originFileObj) {
      const base64File = await fileToBase64(avatarImageFile.originFileObj);
      avatarImageFilePayload = {
        fileName: avatarImageFile.name,
        base64File
      }
    } else {
      avatarImageFilePayload = {
        fileName: avatarImageFile.name
      }
    }

    if (modalAction === "Create") {
      method = "POST";
      payload = {
        performerName: values.performerName,
        isCartoon: values.isCartoon,
        photoFile: photoFilePayload,
        avatarImageFile: avatarImageFilePayload,
      };
    } else {
      method = "PATCH";
      payload = {
        performerId: values.performerId,
        performerName: values.performerName,
        isCartoon: values.isCartoon,
        photoFile: photoFilePayload,
        avatarImageFile: avatarImageFilePayload,
      };
    }

    const data = await faceCallPerformerMutation(method, payload);

    if (data?.status === "SUCCESS") {
      refreshData();
      handleClose();
      message.success(renderAntDMessageConfig("Successful"));
    } else {
      message.error(renderAntDMessageConfig("Failed"));
    }
  };

  useEffect(() => {
    if (searchText === "") setDisplayData(performersData);
    else handleSearch();
  }, [performersData]);

  useEffect(() => {
    if (editData) {
      form.setFieldsValue({
        performerId: editData.performerId,
        performerName: editData.performerName,
        isCartoon: editData.isCartoon,
        ...(editData.photoFile ? {
          photoFile: [
            {
              uid: "0",
              name: editData.photoFile,
              status: "done",
              url: editData.photoFileUrl
            },
          ]
        } : {}),
        ...(editData.avatarImageFile ? {
          avatarImageFile: [
            {
              uid: "0",
              name: editData.avatarImageFile,
              status: "done",
              url: editData.avatarImageUrl
            },
          ]
        } : {}),
      });
    }
  }, [editData, form]);

  return (
    <>
      <div id="faceCallPerfirnersWrapper">
        <div className="table-container">
          <div className="table-menu-container">
            <Search
              placeholder={"input search text"}
              onChange={(e) => {
                setSearchText(e.target.value);
                if (e.target.value === "") {
                  setDisplayData(performersData);
                }
              }}
              onSearch={handleSearch}
              style={{ width: 264 }}
              allowClear
            />
            {isAdmin &&
              <Button
                type="primary"
                style={{ marginLeft: 8 }}
                icon={<PlusOutlined />}
                onClick={() => {
                  setModalAction("Create");
                  setShowDrawer(true);
                  setEditData(undefined);
                }}
              >
                Create New
              </Button>
            }
          </div>
          <List
            grid={{
              gutter: 16,
              xs: 1,
              sm: 2,
              md: 4,
              lg: 4,
              xl: 6,
              xxl: 8,
            }}
            dataSource={displayData}
            renderItem={(item: PerformersType) => (
              <List.Item>
                <Card
                  cover={
                    <div style={{ width: '100%', height: 184, overflow: 'hidden' }}>
                      <img alt="avatar" src={item.photoFileUrl} ref={coverRef}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }} />
                    </div>
                  }
                  style={{ padding: 0 }}
                >
                  <Meta
                    style={{ padding: "8px 12px", margin: 0 }}
                    title={item.performerName}
                    description={
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginTop: 4,
                        }}
                      >
                        <p style={{ color: "#000000" }}>
                          {item.inUseVideosCount} videos
                        </p>
                        <p>{`(${item.totalVideosCount})`}</p>
                      </div>
                    }
                  />
                  <Meta
                    style={{
                      padding: "12px 0",
                      margin: 0,
                      borderTop: "1px solid #F0F0F0",
                    }}
                    description={
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-evenly",
                          alignItems: "center",
                        }}
                      >
                        <Button
                          style={{ border: 0, padding: 0 }}
                          onClick={() => !isAdmin || handleEditClick(item)}
                        >
                          <img
                            alt="edit icon"
                            src={require("../../../assets/icons/edit1.png")}
                            style={{ width: 16, height: 16 }}
                          />
                        </Button>
                        <Divider type="vertical" />
                        <Button
                          style={{
                            border: 0,
                            padding: 0,
                            display: "flex",
                            alignItems: "center",
                          }}
                          onClick={() => handleListClick(item)}
                        >
                          <UnorderedListOutlined style={{ color: "#000000" }} />
                        </Button>
                      </div>
                    }
                  />
                </Card>
              </List.Item>
            )}
          />
        </div>
        <div>
          <Drawer
            title={modalAction === "Create" ? "Create new performer" : "Edit the performer"}
            open={showDrawer}
            onClose={() => handleClose()}
            width={500}
            className="faceCallDrawer"
          >
            <Form
              name="theme-form"
              form={form}
              onFinish={handleSubmit}
              layout="vertical"
            >
              <Form.Item
                name="performerId"
                hidden
              >
                <Input />
              </Form.Item>
              < Form.Item
                className="form-item"
                label="Performer name"
                name="performerName"
                rules={[requiredRule]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                className="form-item"
                label="Performer type"
                name="isCartoon"
                rules={[requiredRule]}
              >
                <Select
                  placeholder="- Select -"
                  options={[
                    { value: false, label: 'Performers' },
                    { value: true, label: 'Zippy and His Friends' },
                  ]}
                />
              </Form.Item>
              <Form.Item
                label="Performer photo"
                name="photoFile"
                className="form-item"
                valuePropName="fileList"
                rules={[requiredRule]}
                getValueFromEvent={(e) => e.fileList}
              >
                <Upload className="performer-upload"
                  {...createUploadProps("png", "photoFile", 1, "picture")}
                >
                  <Button icon={<UploadOutlined />}>Click to Upload</Button>
                </Upload>
              </Form.Item>
              <Form.Item
                label="Avatar image"
                name="avatarImageFile"
                className="form-item"
                valuePropName="fileList"
                rules={[requiredRule]}
                getValueFromEvent={(e) => e.fileList}
              >
                <Upload className="performer-upload"
                  {...createUploadProps("png", "avatarImageFile", 1, "picture")}
                >
                  <Button icon={<UploadOutlined />}>Click to Upload</Button>
                </Upload>
              </Form.Item>
              <DrawerFooter
                handleCancel={() => {
                  form.resetFields();
                  setEditData(undefined);
                  setShowDrawer(false);
                }}
                handleDelete={modalAction === "Edit"
                  ? () => handleDeleteClick(editData!)
                  : undefined}
              />
            </Form>
          </Drawer>
          <GeneralModal
            title={"The performer cannot be deleted"}
            text={"There are more than 1 songs associated with the performer."}
            open={showDeleteModal}
            close={closeDeleteModal}
            handleConfirm={() => closeDeleteModal()}
            isConfirm={true}
            isDeletion={false}
            showNoBtn={false}
            confirmBtnText={"Got it"}
            width={417}
          />
        </div >
      </div >
    </>
  );
};

export default PerformersTabContent;
